// Sales Forecasting Data Types
// This module defines the data structures for the hierarchical sales forecasting tool

/** Time bucket label for column headers (month / 4-week period / quarter / ISO week). */
export type PeriodGrain = "month" | "period" | "quarter" | "week"

export interface TimePeriod {
  year: number
  month: number
  period_key: string // e.g. "2026-07", "2026-Q2", "2026-P04", "2026-W27"
  /** When known (from forecast version or parsed key). */
  grain?: PeriodGrain
  /** Short label for grid headers (avoids assuming monthly `MMM yyyy`). */
  label?: string
}

export interface SalesMetrics {
  store_count: number
  /**
   * Aggregate rows only: Σ of the SKU-level store counts underneath (total points of
   * distribution). `store_count` stays the door count (max across SKUs); velocity divides by
   * this instead so a parent's per-store number is the average of its SKUs', not
   * units-across-16-SKUs over one SKU's doors. Absent on leaf rows.
   */
  sku_store_count?: number
  volume_dollars: number
  cases_sold: number
  /**
   * Aggregate rows only: Σ of the part-cases under this row whose own whole-case count is 0 (see
   * `implied_part_case`). Lets a parent or total whose cases sum to 0 show `0.25` rather than
   * nothing. Absent on leaf rows, which imply their own.
   */
  part_cases?: number
  /** Optional unit count — populated when the actualizer has both unit_price and units_per_case. */
  units_sold?: number | null
  case_cost: number
  /** Retailer forecast CSV breakdown (`detail_metrics`); used when toggling consumption vs delivered, etc. */
  detail?: Record<string, number>
  // Future metrics could include:
  // average_price?: number
  // growth_rate?: number
}

export interface SalesDataPoint {
  period: TimePeriod
  metrics: SalesMetrics
  is_forecast?: boolean
  is_actual?: boolean
  source_kind?: 'actual' | 'forecast' | 'manual_override' | 'top_down_allocated' | 'imported' | string
  data_source?: string
  is_locked?: boolean
  manual_note?: string
  // For comparison mode
  /** When set, Compare UI only renders if the toolbar metric toggle matches (e.g. dollars vs cases). */
  compare_axis?: "dollars" | "cases" | "units"
  forecast_metrics?: SalesMetrics
  actual_metrics?: SalesMetrics
  variance?: {
    store_count_variance: number
    volume_dollars_variance: number
    cases_sold_variance: number
    store_count_variance_pct: number
    volume_dollars_variance_pct: number
    cases_sold_variance_pct: number
  }
  opportunity_base_metrics?: SalesMetrics
}

export type ForecastOpportunityStatus = "open" | "live" | "lost"

export interface ForecastOpportunityMetadata {
  id: string
  name: string
  likelihood_pct: number
  status: ForecastOpportunityStatus
  color: string
  created_from_row_id?: string
  /** Likelihood at the moment the deal was won or lost — restored on reopen. */
  likelihood_pct_at_close?: number
}

// Base interface for all hierarchy levels
export interface HierarchyNode {
  id: string
  name: string
  level: HierarchyLevel
  parent_id?: string
  stable_key?: string
  path_key?: string
  metadata?: {
    opportunity?: ForecastOpportunityMetadata
    /** Customer-node trade spend (see utils/gross_to_net.ts); round-trips through Save. */
    trade_spend?: import("./utils/gross_to_net").TradeSpendMetadata
    [key: string]: unknown
  } | null
  children?: HierarchyNode[]
  sales_data: SalesDataPoint[]
  // Metadata for UI
  is_expanded?: boolean
  is_visible?: boolean
  has_children?: boolean
  // Per-row forecast configuration
  forecast_config?: {
    initial_doors: number
    same_store_sales_multiplier: number
    store_count_growth_multiplier: number
    initial_cases_per_month: number
    cost_per_case: number
  }
}

// Special interface for Distribution Centers that can serve multiple retailers
export interface DistributionCenterNode extends HierarchyNode {
  level: HierarchyLevel.DISTRIBUTION_CENTER
  dc_code?: string
  dc_location?: string
  // Instead of children, we have relationships to retailers
  retailer_relationships?: RetailerRelationship[]
}

// Special interface for Retailers that can be served by multiple DCs
export interface RetailerNode extends HierarchyNode {
  level: HierarchyLevel.RETAILER
  retailer_type?: string
  // Instead of children, we have relationships to DCs
  dc_relationships?: DCRelationship[]
  children?: ProductLineNode[]
}

// Relationship between DC and Retailer
export interface RetailerRelationship {
  retailer_id: string
  retailer_name: string
  sales_data: SalesDataPoint[]
  // Additional relationship metadata
  service_level?: string
  coverage_area?: string
}

export interface DCRelationship {
  dc_id: string
  dc_name: string
  sales_data: SalesDataPoint[]
  // Additional relationship metadata
  service_level?: string
  coverage_area?: string
}

export enum HierarchyLevel {
  CLASS_OF_TRADE = 'class_of_trade',
  DISTRIBUTOR = 'distributor', 
  DISTRIBUTION_CENTER = 'distribution_center',
  CUSTOMER = 'customer',
  RETAILER = 'retailer',
  PRODUCT_LINE = 'product_line',
  SKU = 'sku'
}

// Specific hierarchy level interfaces
export interface ClassOfTradeNode extends HierarchyNode {
  level: HierarchyLevel.CLASS_OF_TRADE
  children?: (DistributorNode | CustomerNode)[]
}

export interface DistributorNode extends HierarchyNode {
  level: HierarchyLevel.DISTRIBUTOR
  parent_id: string // References ClassOfTradeNode
  children?: DistributionCenterNode[]
}

export interface CustomerNode extends HierarchyNode {
  level: HierarchyLevel.CUSTOMER
  parent_id: string // References ClassOfTradeNode
  children?: ProductLineNode[]
}

export interface DistributionCenterNode extends HierarchyNode {
  level: HierarchyLevel.DISTRIBUTION_CENTER
  parent_id: string // References DistributorNode
  dc_code?: string
  dc_location?: string
  children?: RetailerNode[]
}

export interface RetailerNode extends HierarchyNode {
  level: HierarchyLevel.RETAILER
  parent_id: string // References DistributionCenterNode
  retailer_type?: string
  children?: ProductLineNode[]
}

export interface ProductLineNode extends HierarchyNode {
  level: HierarchyLevel.PRODUCT_LINE
  product_category?: string
  children?: SkuNode[] // Can have SKU children when planning by SKU
}

export interface SkuNode extends HierarchyNode {
  level: HierarchyLevel.SKU
  parent_id: string // Must have a parent (product line)
  product_sku?: string
  sku_name?: string
  case_cost?: number
  // No children - this is the leaf level
}

// Union type for all hierarchy nodes
export type SalesHierarchyNode = 
  | ClassOfTradeNode 
  | DistributorNode 
  | DistributionCenterNode 
  | RetailerNode 
  | ProductLineNode
  | SkuNode

// Data access permissions for different brands
export interface DataAccessLevel {
  brand_id: string
  max_granularity_level: HierarchyLevel
  available_distributors?: string[]
  available_classes_of_trade?: string[]
  // This determines how deep the brand can see in the hierarchy
}

// Configuration for the forecasting tool
export interface ForecastingConfig {
  selected_periods: TimePeriod[]
  selected_hierarchy_levels: HierarchyLevel[]
  data_access_level: DataAccessLevel
  aggregation_method: 'sum' | 'average' | 'weighted_average'
  show_forecast: boolean
  show_actual: boolean
  show_variance: boolean
}

// API response structure
export interface SalesForecastingData {
  hierarchy: SalesHierarchyNode[]
  config: ForecastingConfig
  metadata: {
    last_updated: string
    data_quality_score: number
    missing_data_periods: TimePeriod[]
  }
}

// UI-specific types for the data table
export interface TableRow {
  id: string
  name: string
  level: HierarchyLevel
  depth: number
  parent_id?: string
  is_expanded: boolean
  is_visible: boolean
  has_children: boolean
  // Time series data as columns
  monthly_data: {
    [period_key: string]: {
      store_count: number
      /** Σ SKU-level store counts (velocity denominator on aggregate rows); absent on leaves. */
      sku_store_count?: number
      volume_dollars: number
      cases_sold: number
      /** Part-cases behind a zero case count on this row (its own, or summed from below). */
      part_cases?: number
      units_sold?: number | null
      is_forecast: boolean
      is_actual: boolean
      /** Mirrors `SalesDataPoint.is_locked`; a fill-forward skips locked periods. */
      is_locked?: boolean
      compare_axis?: "dollars" | "cases" | "units"
      // Comparison data
      forecast_metrics?: SalesMetrics
      actual_metrics?: SalesMetrics
      variance?: {
        store_count_variance: number
        volume_dollars_variance: number
        cases_sold_variance: number
        store_count_variance_pct: number
        volume_dollars_variance_pct: number
        cases_sold_variance_pct: number
      }
      /** Retailer `detail_metrics` copy for promo lift / slice UI. */
      detail?: Record<string, number>
    }
  }
  // Aggregated totals
  totals: {
    total_stores: number
    /** Max across periods of Σ SKU-level store counts (velocity denominator on aggregates). */
    total_sku_stores?: number
    total_volume: number
    total_cases: number
    /** Σ part-cases across periods, for a row whose whole cases total 0. */
    total_part_cases?: number
    /** Sum of resolved display units across periods (for totals column when quantity_mode is units). */
    total_units: number
    /** Sum of imputed promo revenue lift across periods (detail incremental promo × cell $/unit). */
    total_promo_lift_dollars: number
    /** Sum of incremental promo units from detail across periods. */
    total_promo_lift_units: number
    average_stores_per_period: number
    average_volume_per_period: number
    average_cases_per_period: number
  }
  // Per-row forecast configuration
  forecast_config?: {
    initial_doors: number
    same_store_sales_multiplier: number
    store_count_growth_multiplier: number
    initial_cases_per_month: number
    cost_per_case: number
  }
  // Case cost for this row (only applicable to product line level)
  case_cost?: number
  metadata?: {
    opportunity?: ForecastOpportunityMetadata
    [key: string]: unknown
  } | null
  product_sku?: string
  sku_name?: string
  /** Product-line aggregate view: real hierarchy node ids backing this row (for deletes). */
  source_ids?: string[]
  /** Relationship slice rows; subtree delete is not supported for these yet. */
  original_retailer_id?: string
  original_dc_id?: string
}

// View modes for DC vs Retailer breakdown
export enum ViewMode {
  CHAIN_RETAILER_VIEW = 'chain_retailer_view',  // Class of Trade → Retailer → Product Lines
  PRODUCT_LINE_VIEW = 'product_line_view',      // Product Lines aggregated across all hierarchy levels
  SHIP_TO_VIEW = 'ship_to_view'                 // Distributor (ship-to) → Customer, from RTM allocations
}

// Forecast configuration
export interface ForecastConfig {
  same_store_sales_multiplier: number  // e.g., 0.03 for 3% growth
  store_count_multiplier: number       // e.g., 0.02 for 2% growth
  forecast_start_period: string        // e.g., "2025-01"
  enabled: boolean
}

/** Which metric the cell shows when only one number is rendered. */
export type MetricMode = "dollars" | "units" | "cases"

// Column configuration for table display
export interface ColumnConfig {
  show_stores: boolean
  /** @deprecated Use quantity_mode; kept for compatibility with saved prefs. */
  show_cases: boolean
  show_volume: boolean // Always true — revenue is always shown in period cells
  /**
   * Secondary quantity row: **units**, **packs** or **cases** (mutually exclusive). Revenue is always shown separately.
   * Synced with Review $/Units/Cases when those are Units or Cases; Columns toolbar owns this toggle.
   * Packs is Units seen through each item's inner pack (`utils/catalog_overlay.ts`).
   */
  quantity_mode?: "units" | "cases" | "packs"
  /** Compare axis + slice visibility: `dollars` | `units` | `cases`. Revenue in cells is always shown regardless. */
  metric_mode?: MetricMode
  /**
   * Show quantity per store per period under each cell, and let it be edited.
   * Derived from stores × quantity — see `utils/velocity.ts`.
   */
  show_velocity?: boolean
  /** When `metrics.detail` includes retailer import keys — dollar column source. */
  forecast_dollar_basis?: "consumption_gross_sales" | "gross_sales_delivered"
  forecast_case_basis?: "forecasted_consumption_cases" | "forecasted_delivery_cases"
  forecast_unit_basis?:
    | "forecasted_consumption_units"
    | "seasonality_adjusted_baseline_consumption_units"
    | "incremental_promotion_consumption_units"
}

// Version management
export interface ForecastVersion {
  id: string
  name: string
  description?: string
  scenario_kind?: "operating_plan" | "growth_story" | "board_case" | string
  parent_version_id?: string
  fiscal_year?: number
  forecast_start_period?: string
  actuals_through_period?: string
  /** `month` | `period` (4-week) | `quarter` | `week` */
  period_grain?: string
  locked_at?: string
  archived_at?: string
  created_at: string
  updated_at: string
  /** One primary per plan kind: the primary forecast and the primary budget are separate. */
  is_primary: boolean
  /** `forecast` (default) | `budget`. */
  plan_kind?: string
  data: SalesHierarchyNode[]
  periods: TimePeriod[]
  compare_mode: boolean
  comparison_periods: TimePeriod[]
  column_config?: ColumnConfig
}

export interface VersionManager {
  versions: ForecastVersion[]
  current_version_id: string | null
  primary_version_id: string | null
}

// Filter and view state
export interface ForecastingViewState {
  expanded_nodes: Set<string>
  visible_levels: Set<HierarchyLevel>
  selected_periods: TimePeriod[]
  search_term: string
  sort_by: 'stores'
  sort_direction: 'asc' | 'desc'
  show_forecast_only: boolean
  show_actual_only: boolean
  view_mode: ViewMode  // DC view or Retailer view
  forecast_config: ForecastConfig
  // Comparison mode
  compare_mode: boolean
  comparison_periods: TimePeriod[] // Periods where we have actual data
  // Column configuration
  column_config: ColumnConfig
}

// Utility types for data processing
export interface AggregationResult {
  period: TimePeriod
  total_stores: number
  total_volume: number
  node_count: number
}

export interface HierarchyPath {
  class_of_trade: string
  distributor?: string
  distribution_center?: string
  retailer?: string
  product_line?: string
}

// Error handling
export interface DataError {
  node_id: string
  level: HierarchyLevel
  error_type: 'missing_data' | 'invalid_data' | 'access_denied'
  message: string
  affected_periods: TimePeriod[]
}
